import axios from 'axios';

/**
 * ApiError - Custom error class for API errors
 *
 * Provides structured error information from backend responses
 */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly errors?: Record<string, string[]>;
  public readonly traceId?: string;

  constructor(
    message: string,
    statusCode: number,
    errors?: Record<string, string[]>,
    traceId?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errors = errors;
    this.traceId = traceId;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (typeof Error.captureStackTrace !== 'undefined') {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Check if error is an ApiError instance
   */
  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }

  /**
   * Get all error messages as a flat array
   */
  getAllErrorMessages(): string[] {
    const messages: string[] = [this.message];

    if (this.errors) {
      Object.values(this.errors).forEach((errorArray) => {
        messages.push(...errorArray);
      });
    }

    return messages;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    // Use custom message if provided, otherwise use status-based message
    if (this.message && this.message !== 'Request failed') {
      return this.message;
    }

    return getDefaultErrorMessage(this.statusCode);
  }
}

/**
 * Get default error message based on status code
 */
function getDefaultErrorMessage(statusCode: number): string {
  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input.',
    401: 'You are not authenticated. Please log in.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This action conflicts with existing data.',
    422: 'The data provided is invalid.',
    429: 'Too many requests. Please try again later.',
    500: 'An internal server error occurred. Please try again.',
    502: 'Bad gateway. The server is temporarily unavailable.',
    503: 'Service unavailable. Please try again later.',
    504: 'Gateway timeout. The request took too long.'
  };

  return statusMessages[statusCode] ?? 'An unexpected error occurred.';
}

/**
 * Network error - when request fails before reaching server
 */
export class NetworkError extends Error {
  constructor(message = 'Network error. Please check your connection.') {
    super(message);
    this.name = 'NetworkError';

    if (typeof Error.captureStackTrace !== 'undefined') {
      Error.captureStackTrace(this, NetworkError);
    }
  }

  static isNetworkError(error: unknown): error is NetworkError {
    return error instanceof NetworkError;
  }
}

/**
 * Timeout error - when request exceeds timeout limit
 */
export class TimeoutError extends Error {
  constructor(message = 'Request timeout. Please try again.') {
    super(message);
    this.name = 'TimeoutError';

    if (typeof Error.captureStackTrace !== 'undefined') {
      Error.captureStackTrace(this, TimeoutError);
    }
  }

  static isTimeoutError(error: unknown): error is TimeoutError {
    return error instanceof TimeoutError;
  }
}

/**
 * The HTTP status behind a failed request, or undefined when there was no response at all.
 *
 * sprint-273 S006 (goal 2): callers used to test `error.message.includes('404')`, which breaks the
 * moment the message wording changes. The status is on the error; read it from there.
 */
export function httpStatusOf(error: unknown): number | undefined {
  if (ApiError.isApiError(error)) {
    return error.statusCode;
  }
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}
